import { Head, Link, router } from "@inertiajs/react"
import { MessageCircle, MessagesSquare, Plus, Search } from "lucide-react"
import { useState, useEffect, useRef } from "react"
import { formatDistanceToNow } from "date-fns"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { EmptyState } from "@/components/empty-state"
import { PageHeader } from "@/components/page-header"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"
import { conversationPath, newConversationPath } from "@/routes"

interface ConversationItem {
  id: string
  title: string
  updated_at: string
  message_count: number
}

interface ConversationsIndexProps {
  conversations: ConversationItem[]
  total: number
  page: number
  per_page: number
  search?: string
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Conversations",
    href: "/conversations",
  },
]

export default function ConversationsIndex({
  conversations,
  total,
  page,
  per_page,
  search: initialSearch,
}: ConversationsIndexProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearch || "")
  const [allConversations, setAllConversations] =
    useState<ConversationItem[]>(conversations)
  const [currentPage, setCurrentPage] = useState(page)
  const hasMore = currentPage * per_page < total
  const hintShownRef = useRef(false)

  // Show hint toast when coming from flashcards page
  useEffect(() => {
    if (hintShownRef.current) return
    const params = new URLSearchParams(window.location.search)
    if (params.get("hint") === "flashcard") {
      toast.info(
        "Open a conversation and select a message to create flashcards",
        {
          duration: 5000,
        },
      )
      hintShownRef.current = true
      // Clean up URL
      params.delete("hint")
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname
      window.history.replaceState({}, "", newUrl)
    }
  }, [])

  // Reset accumulated conversations when search changes or when starting fresh
  useEffect(() => {
    if (page === 1) {
      setAllConversations(conversations)
      setCurrentPage(1)
    } else {
      // Append new conversations when loading more
      setAllConversations((prev) => {
        // Avoid duplicates
        const existingIds = new Set(prev.map((c) => c.id))
        const newConversations = conversations.filter(
          (c) => !existingIds.has(c.id),
        )
        return [...prev, ...newConversations]
      })
      setCurrentPage(page)
    }
  }, [conversations, page])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.visit("/conversations", {
      data: { search: searchTerm || undefined, page: 1 },
      preserveState: true,
    })
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    router.visit("/conversations", {
      data: { page: 1 },
      preserveState: true,
    })
  }

  const handleShowMore = () => {
    router.visit("/conversations", {
      data: { search: searchTerm || undefined, page: currentPage + 1 },
      preserveState: true,
      preserveScroll: true,
      only: ["conversations", "page"],
    })
  }

  const handleConversationClick = (id: string) => {
    router.visit(conversationPath(id))
  }

  // Empty state when no conversations exist
  if (allConversations.length === 0 && !initialSearch) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title="Conversations" />
        <div className="flex h-full flex-1 items-center justify-center">
          <EmptyState
            icon={<MessagesSquare className="size-16" />}
            title="Start your first chat with ThaiCopilot!"
            description="Begin your language learning journey by creating your first conversation."
            action={
              <Button asChild>
                <Link href={newConversationPath()}>
                  <Plus className="size-4" />
                  New Conversation
                </Link>
              </Button>
            }
          />
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Conversations" />

      <div className="flex h-full flex-1 flex-col">
        <PageHeader
          title="Conversations"
          description="Your Thai learning conversations"
          action={
            <Button asChild size="sm">
              <Link href={newConversationPath()}>
                <Plus className="size-4" />
                New Conversation
              </Link>
            </Button>
          }
        />

        <div className="mx-auto w-full max-w-5xl flex-1 px-4 sm:px-6 lg:px-8">
          {/* Search Bar */}
          <div className="border-border border-b py-4">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="Search conversations…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10"
                />
                <Search className="text-muted-foreground absolute top-1/2 right-3 size-4 -translate-y-1/2" />
              </div>
              <Button type="submit" variant="outline">
                Search
              </Button>
            </form>
          </div>

          {/* Metadata */}
          <div className="text-muted-foreground py-3 text-sm">
            {total} {total === 1 ? "conversation" : "conversations"} with
            ThaiCopilot
            {initialSearch && (
              <>
                {" · "}
                <span className="text-foreground">
                  {allConversations.length} result
                  {allConversations.length !== 1 ? "s" : ""} for "
                  {initialSearch}"
                </span>
              </>
            )}
          </div>

          {/* Conversation List */}
          <ScrollArea className="flex-1">
            <div className="space-y-2 py-4">
              {allConversations.length === 0 ? (
                <EmptyState
                  icon={<MessageCircle className="size-12" />}
                  title={`No conversations found matching "${initialSearch}"`}
                  action={
                    <Button onClick={handleClearSearch} variant="link">
                      Clear filter
                    </Button>
                  }
                />
              ) : (
                allConversations.map((conversation) => (
                  <Card
                    key={conversation.id}
                    className="hover:bg-accent/5 cursor-pointer transition-all"
                    onClick={() => handleConversationClick(conversation.id)}
                  >
                    <div className="flex items-start justify-between p-4">
                      <div className="flex-1 space-y-1">
                        <h3 className="font-medium">{conversation.title}</h3>
                        <p className="text-muted-foreground text-sm">
                          {formatDistanceToNow(
                            new Date(conversation.updated_at),
                            {
                              addSuffix: true,
                            },
                          )}
                          {conversation.message_count > 0 && (
                            <span className="ml-2">
                              · {conversation.message_count}{" "}
                              {conversation.message_count === 1
                                ? "message"
                                : "messages"}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>

            {/* Show More Button */}
            {hasMore && allConversations.length > 0 && (
              <div className="flex justify-center pb-6">
                <Button onClick={handleShowMore} variant="outline">
                  Show more
                </Button>
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </AppLayout>
  )
}
